import { useEffect, useState, useCallback } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { RiVoiceprintLine, RiGlobalLine } from "react-icons/ri";
import { MdKeyboardVoice } from "react-icons/md";
import { BiChip } from "react-icons/bi";
import { IoArrowForward } from "react-icons/io5";
import { IoCloseOutline } from "react-icons/io5";

import { Link } from "react-router-dom";
import { RiFilePdf2Line, RiAttachment2 } from "react-icons/ri";

import axios from "axios";
import { Tooltip } from "react-tooltip";
import TransactionsModal from "../modelpopup/TransactionsModal";

const promptIntro = `
  أنت مستشار ذو خبرة 30 عامًا في التحليل المالي والتحقيقات المالية. خبير عالمي في الضرائب الدولية، التكنولوجيا المالية (Fintech)، العملات الرقمية، المحاسبة والتدقيق. عملت مع الحكومات، البنوك، الشركات الكبرى، وفرق مكافحة الجرائم المالية. أنت لا ترتكب أخطاء. دقتك لا تُضاهى. اكتب الرد التالي وكأن سمعتك العالمية ومصداقيتك المهنية على المحك. لا تقدم كلامًا عامًا أو غير مدعوم — فقط تحليلات دقيقة، واقعية، ومبنية على أفضل الممارسات العالمية.
  أجب السؤال الآتي:\n
`;

// Use environment variable with fallback for development
const GEMINI_API =
  process.env.REACT_APP_GEMINI_API_KEY ||
  "AIzaSyAetO_cbRT-jKsFQEdWN4W-G5AonyU3Fgk";
const OPEN_AI = process.env.REACT_APP_OPEN_AI_API_KEY;

// File validation constants
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "text/plain",
];

export default function BookBotSearchBar({
  onFileUpload,
  triggerUpload,
  reset,
  fileTypeObject,
}) {
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [response, setResponse] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [processingStage, setProcessingStage] = useState("");

  // Enhanced loading progress simulation
  const simulateProgress = useCallback((stages) => {
    let currentStage = 0;
    const progressInterval = setInterval(() => {
      if (currentStage < stages.length) {
        const stage = stages[currentStage];
        setProcessingStage(stage.stage);
        setLoadingMessage(stage.message);
        setLoadingProgress(stage.progress);
        currentStage++;
      } else {
        clearInterval(progressInterval);
      }
    }, 1500); // Change stage every 1.5 seconds

    return progressInterval;
  }, []);

  // Reset loading states
  const resetLoadingStates = useCallback(() => {
    setLoading(false);
    setLoadingProgress(0);
    setLoadingMessage("");
    setProcessingStage("");
  }, []);

  // File validation function
  const validateFile = useCallback((file) => {
    if (file.size > MAX_FILE_SIZE) {
      console.error(
        `File ${file.name} is too large. Max size: ${
          MAX_FILE_SIZE / 1024 / 1024
        }MB`
      );
      return false;
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      console.error(`File ${file.name} has unsupported type: ${file.type}`);
      return false;
    }
    return true;
  }, []);

  const askOpenAi = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Start progress simulation for text analysis
    const progressStages = [
      { stage: "connecting", message: "الاتصال بـ OpenAI...", progress: 20 },
      {
        stage: "analyzing",
        message: "تحليل النص باستخدام GPT-4...",
        progress: 60,
      },
      { stage: "processing", message: "معالجة النتائج...", progress: 90 },
    ];

    const progressInterval = simulateProgress(progressStages);

    try {
      const apiUrl = "https://api.openai.com/v1/chat/completions";

      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${OPEN_AI}`,
      };

      const requestBody = {
        model: "gpt-4",
        messages: [
          {
            role: "system",
            content: promptIntro,
          },
          {
            role: "user",
            content: input,
          },
        ],
        max_tokens: 500,
        temperature: 0.7,
        top_p: 1.0,
        n: 1,
      };

      const { data } = await axios.post(apiUrl, requestBody, { headers });

      // Complete progress
      setLoadingProgress(100);
      setLoadingMessage("تم الانتهاء!");

      setTimeout(() => {
        setResponse(data.choices[0].message.content);
        setShowModal(true);
        resetLoadingStates();
      }, 500);
    } catch (error) {
      console.error(
        "Error sending message:",
        error.response?.data || error.message
      );
      resetLoadingStates();
    } finally {
      clearInterval(progressInterval);
    }
  };

  const handleGeminiAsking = async (e) => {
    e.preventDefault();
    if (file && file.length > 0) {
      askGeminiWithFiles(e);
    } else {
      askGemini(e);
    }
  };

  const askGemini = async (e) => {
    e.preventDefault();

    const genAI = new GoogleGenerativeAI(GEMINI_API);
    setLoading(true);

    // Start progress simulation for text analysis
    const progressStages = [
      { stage: "connecting", message: "الاتصال بـ Gemini AI...", progress: 25 },
      {
        stage: "analyzing",
        message: "تحليل النص باستخدام Gemini 2.0...",
        progress: 70,
      },
      { stage: "finalizing", message: "إنهاء المعالجة...", progress: 95 },
    ];

    const progressInterval = simulateProgress(progressStages);

    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });

      const result = await model.generateContent(
        `${fileTypeObject["prompt_ar"]}`
      );

      const textResponse = result.response.text();
      console.log(result);

      // Complete progress
      setLoadingProgress(100);
      setLoadingMessage("تم الانتهاء!");

      setTimeout(() => {
        setResponse(textResponse);
        setShowModal(true);
        resetLoadingStates();
      }, 500);
    } catch (err) {
      console.error("Error calling Gemini API:", err);
      resetLoadingStates();
    } finally {
      clearInterval(progressInterval);
    }
  };

  useEffect(() => {
    if (triggerUpload) {
      handleAddFile();
    }
  }, [triggerUpload]);

  const askGeminiWithFiles = async (e) => {
    e.preventDefault();
    const genAI = new GoogleGenerativeAI(GEMINI_API);
    setLoading(true);

    // Enhanced progress tracking for file processing
    const totalFiles = file.length;
    let processedFiles = 0;

    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });
      const parts = [];

      // Initial setup
      setProcessingStage("preparing");
      setLoadingMessage("تحضير الملفات للمعالجة...");
      setLoadingProgress(5);

      const textPrompt = `
      You are a smart accountant with 30 years of experience. Read the attached file, which is a list of expense invoices. Some invoices may also have attached payment details. After thoroughly reading all the invoices, create a JSON file (do not append to an existing file) that includes all the invoice details in the following exact format, with all values extracted from the document:

{
  "document_name": "",
  "type": "",
  "bank_transfer_details": {
    "request_received_date": "",
    "sender": "",
    "sender_account": "",
    "sender_bank": "",
    "amount": "",
    "recipient": "",
    "recipient_account": "",
    "recipient_bank": "",
    "transfer_execution_date": "",
    "reference_number": "",
    "transfer_details": "",
    "total_fees": ""
  },
  "invoice_receipt_details": {
    "document_type": "",
    "date": "",
    "time": "",
    "invoice_number": "",
    "customer_number": "",
    "supplier": "",
    "supplier_address": "",
    "supplier_phone": "",
    "supplier_fax": "",
    "supplier_authorized_dealer_number": "",
    "recipient_name": "",
    "recipient_address": "",
    "items": [
      {
        "item_number": "",
        "description": "",
        "quantity": "",
        "unit_price_ils": "",
        "total_ils": ""
      }
    ],
    "total_before_vat": "",
    "discount_percentage": "",
    "discount_amount": "",
    "total_after_discount": "",
    "vat_percentage": "",
    "vat_amount": "",
    "total_due": "",
    "payment_details": [
      {
        "line_details": "",
        "payment_type": "",
        "payment_date": "",
        "amount_ils": ""
      }
    ]
  }
}
`;
      parts.push({ text: textPrompt });

      // Process files with progress tracking
      setProcessingStage("uploading");
      setLoadingMessage(`معالجة الملفات (0/${totalFiles})...`);

      for (const f of file) {
        setLoadingMessage(
          `معالجة الملف: ${f.name} (${processedFiles + 1}/${totalFiles})`
        );
        const fileProgress = 10 + (processedFiles / totalFiles) * 40; // 10-50% for file processing
        setLoadingProgress(fileProgress);

        const base64Data = await readFileAsBase64(f);
        parts.push({
          inlineData: {
            mimeType: f.type,
            data: base64Data.split(",")[1],
          },
        });

        processedFiles++;
      }

      // AI Analysis phase
      setProcessingStage("analyzing");
      setLoadingMessage("تحليل المستندات باستخدام الذكاء الاصطناعي...");
      setLoadingProgress(60);

      const result = await model.generateContent({
        contents: [{ role: "user", parts }],
      });

      // Data extraction phase
      setProcessingStage("extracting");
      setLoadingMessage("استخراج البيانات من النتائج...");
      setLoadingProgress(85);

      const textResponse = result.response.text();
      const json = extractJSONFromText(textResponse);

      // Saving phase
      setProcessingStage("saving");
      setLoadingMessage("حفظ البيانات...");
      setLoadingProgress(95);

      if (json) {
        window.localStorage.setItem("expensesData", JSON.stringify(json));
        console.log("Successfully extracted and saved JSON:", json);
      } else {
        console.warn("No valid JSON found in response");
      }

      // Complete
      setLoadingProgress(100);
      setLoadingMessage("تم الانتهاء بنجاح!");

      setTimeout(() => {
        setResponse(textResponse);
        setShowModal(true);
        resetLoadingStates();
        setFile([]); // Clear files after processing
      }, 800);
    } catch (err) {
      console.error("Error calling Gemini API:", err);
      resetLoadingStates();
      setFile([]);
    }
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  function extractJSONFromText(text) {
    const stack = [];
    let startIndex = -1;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (char === "{" || char === "[") {
        if (stack.length === 0) startIndex = i;
        stack.push(char);
      } else if (char === "}" || char === "]") {
        const last = stack[stack.length - 1];
        if ((char === "}" && last === "{") || (char === "]" && last === "[")) {
          stack.pop();
          if (stack.length === 0) {
            const jsonCandidate = text.slice(startIndex, i + 1);
            try {
              const parsed = JSON.parse(jsonCandidate);
              return parsed; // First valid JSON block found
            } catch (err) {
              // Ignore and continue
            }
          }
        } else {
          // Mismatched brackets, reset
          stack.length = 0;
        }
      }
    }

    return null; // No valid JSON found
  }

  const handleAddFile = useCallback(() => {
    const inputFile = document.createElement("input");
    inputFile.type = "file";
    inputFile.multiple = true;
    inputFile.accept = ALLOWED_FILE_TYPES.join(",");

    inputFile.onchange = () => {
      const selectedFiles = inputFile.files;
      if (selectedFiles && selectedFiles.length > 0) {
        const fileArray = Array.from(selectedFiles);

        // Validate files before adding
        const validFiles = fileArray.filter(validateFile);

        if (validFiles.length > 0) {
          setFile((prev) => [...prev, ...validFiles]);

          if (fileTypeObject?.inputText) {
            setInput(fileTypeObject.inputText);
          }

          const promptInput = document.getElementById("prompt-input");
          if (promptInput) {
            promptInput.focus();
          }

          if (onFileUpload) {
            validFiles.forEach((f) => onFileUpload(f));
          }

          window.scrollTo({ top: 0, behavior: "smooth" });
        }

        if (validFiles.length !== fileArray.length) {
          console.warn(
            `${
              fileArray.length - validFiles.length
            } files were rejected due to validation errors`
          );
        }
      }
    };

    inputFile.click();
  }, [validateFile, fileTypeObject, onFileUpload]);

  useEffect(() => {
    if (reset) {
      setInput("");
      setFile([]);
      setResponse("");
      setShowModal(false);
      resetLoadingStates();
    }
  }, [reset, resetLoadingStates]);

  return (
    <div className="row">
      <h1 className="ai-title">
        <span>BookBot</span>
      </h1>
      <div
        className={
          loading
            ? "loading-opacity col-sm-12 col-md-12 mp-3"
            : "col-sm-12 col-md-12"
        }
      >
        <div className="d-flex justify-content-center  mt-3 search-component ">
          <form className="w-100" onSubmit={handleGeminiAsking}>
            <div
              className={
                file
                  ? "search-box position-relative  height-more "
                  : "search-box position-relative"
              }
            >
              <input
                type="text"
                className="form-control border-0 search-bar-input text-white"
                placeholder="اسأل Mass..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                id="prompt-input"
              />

              <div
                className={
                  file.length > 0
                    ? "d-flex   gap-2 mt-3 search-bar-div justify-content-between"
                    : "d-flex   gap-2 mt-3 search-bar-div "
                }
              >
                {file.length > 0 && (
                  <div className="file-preview-inside">
                    {file.map((f, idx) => (
                      <div key={idx} className="d-flex align-items-start mb-2">
                        {f.type === "application/pdf" ? (
                          <RiFilePdf2Line
                            size={20}
                            className="text-danger me-2"
                          />
                        ) : (
                          <RiAttachment2 size={20} className="me-2" />
                        )}
                        <div
                          className="text-truncate"
                          style={{ maxWidth: "120px" }}
                        >
                          <div className="fw-bold small text-white">
                            {f.name}
                          </div>
                          <div className="small text-muted">
                            {(f.size / 1024 / 1024).toFixed(2)} MB
                          </div>
                        </div>
                        <button
                          className="btn btn-sm ms-2"
                          onClick={() =>
                            setFile((prevFiles) =>
                              prevFiles.filter((_, i) => i !== idx)
                            )
                          }
                          type="button"
                        >
                          <IoCloseOutline
                            className="btn-close-file"
                            size={14}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="d-flex gap-2 search-bar-btns flex-row-reverse mb-4 mt-2">
                  <Link
                    to="#"
                    className="active"
                    data-tooltip-id="shared-tooltip"
                    data-tooltip-content="Enter"
                    onClick={handleGeminiAsking}
                  >
                    <IoArrowForward size={20} />
                  </Link>

                  <Link
                    to="#"
                    data-tooltip-id="shared-tooltip"
                    data-tooltip-content="Voice Mode"
                  >
                    <RiVoiceprintLine size={20} />
                  </Link>

                  <Link
                    to="#"
                    data-tooltip-id="shared-tooltip"
                    data-tooltip-content="Dictation"
                  >
                    <MdKeyboardVoice size={20} />
                  </Link>

                  <Link
                    to="#"
                    data-tooltip-id="shared-tooltip"
                    data-tooltip-content="Attach files"
                    onClick={handleAddFile}
                  >
                    <RiAttachment2 size={20} />
                  </Link>

                  <Link
                    to="#"
                    data-tooltip-id="shared-tooltip"
                    data-tooltip-content="Set sources for research"
                  >
                    <RiGlobalLine size={20} />
                  </Link>

                  <Link
                    to="#"
                    data-tooltip-id="shared-tooltip"
                    data-tooltip-content="Choose a model"
                  >
                    <BiChip size={20} />
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      {loading ? (
        <div className="col-12 mt-4">
          <div className="card border-0 shadow-lg">
            <div className="card-body text-center py-5">
              {/* Main Progress Circle */}
              <div className="position-relative d-inline-block mb-4">
                <svg
                  width="120"
                  height="120"
                  className="position-absolute me-4"
                >
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="#e9ecef"
                    strokeWidth="8"
                  />
                  <circle
                    cx="60"
                    cy="60"
                    r="50"
                    fill="none"
                    stroke="var(--bs-primary)"
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 50}`}
                    strokeDashoffset={`${
                      2 * Math.PI * 50 * (1 - loadingProgress / 100)
                    }`}
                    style={{
                      transition: "stroke-dashoffset 0.5s ease-in-out",
                      transform: "rotate(-90deg)",
                      transformOrigin: "60px 60px",
                    }}
                  />
                </svg>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ width: "120px", height: "120px" }}
                >
                  <div className="text-center">
                    <div className="h4 mb-0 text-primary fw-bold">
                      {Math.round(loadingProgress)}%
                    </div>
                    <small className="text-muted">مكتمل</small>
                  </div>
                </div>
              </div>

              {/* Processing Stage Indicator */}
              <div className="mb-3">
                <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                  {[
                    "preparing",
                    "uploading",
                    "analyzing",
                    "extracting",
                    "saving",
                  ].map((stage, index) => (
                    <div key={stage} className="d-flex align-items-center">
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center ${
                          processingStage === stage
                            ? "bg-primary text-white"
                            : loadingProgress > (index + 1) * 20
                            ? "bg-success text-white"
                            : "bg-light text-muted"
                        }`}
                        style={{
                          width: "32px",
                          height: "32px",
                          fontSize: "12px",
                        }}
                      >
                        {loadingProgress > (index + 1) * 20 ? "✓" : index + 1}
                      </div>
                      {index < 4 && (
                        <div
                          className={`mx-2 ${
                            loadingProgress > (index + 1) * 20
                              ? "bg-success"
                              : "bg-light"
                          }`}
                          style={{ width: "30px", height: "2px" }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Message */}
              <div className="mb-4">
                <h5 className="text-primary mb-2">
                  {loadingMessage || "جاري المعالجة..."}
                </h5>
                <p className="text-muted mb-0">
                  يرجى الانتظار بينما نقوم بتحليل مستنداتك باستخدام الذكاء
                  الاصطناعي
                </p>
              </div>

              {/* Animated Dots */}
              <div className="d-flex justify-content-center gap-2 mb-3">
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className="spinner-grow text-primary"
                    style={{
                      width: "0.8rem",
                      height: "0.8rem",
                      animationDelay: `${i * 0.1}s`,
                      opacity: 0.25 + i * 0.075,
                    }}
                    role="status"
                  />
                ))}
              </div>

              {/* File Count (if processing files) */}
              {file.length > 0 && (
                <div className="mt-3">
                  <small className="text-muted">
                    معالجة {file.length} ملف{file.length > 1 ? "ات" : ""}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <TransactionsModal
          show={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
      <Tooltip id="shared-tooltip" place="top" className="my-tooltip-custom" />
    </div>
  );
}
